import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useNavigate } from "react-router-dom";
import { DEFAULT_PAGE_SIZE, urlEndPoint } from "helpers/GeneralHelper";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import CompanyAddEditModal from "pages/AllCommonModal/CompanyAddEditModal/company-add-edit-modal";
import Spinners from "components/Common/Spinner";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { useCompanyContext } from "libs/context/CompanyContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import { submitCompanyFilterData } from "model/CompanyModel";
import TableContainer from "components/Common/TableContainer";
import Loaders from "components/Common/Loaders";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const Company = () => {
  const navigate = useNavigate();
  const { permissionData } = usePermissionContext() || {};
  const { fetchCompanyContextData, companyContextData } = useCompanyContext() || {};
  const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("Company");
  const initialFilterFetchedRef = useRef(false);
  const [companyAddEditModalOpen, setCompanyAddEditModalOpen] = useState(false);
  const [modal1, setModal1] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [companyData, setCompanyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemId, setItemId] = useState(null);
  const [tableMetaData, setTableMetadata] = useState();
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });
  const toggleViewModal = () => setModal1(!modal1);

  const filteredData = useMemo(() => companyData, [companyData]);

  const companyActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.ClientCompanyAddAccess),
      canEdit: Boolean(permissionData?.ClientCompanyUpdateAccess),
      canViewUsers: Boolean(
        permissionData?.ClientCompanyUserViewAccess ||
          permissionData?.ClientCompanyUserAccess
      ),
    }),
    [permissionData]
  );

  const hasAnyCompanyRowAction = useMemo(
    () =>
      companyActionPermissions.canEdit || companyActionPermissions.canViewUsers,
    [companyActionPermissions]
  );

  useEffect(() => {
    fetchCompanyContextData?.();
  }, []);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    getCompanyList({ pageNumber: 1, pageSize });
  }, [settingsReady, settingsPageSize]);

  useEffect(() => {
    // const companyContextData1 = {table : {columns : [
    //     {title: "Id", field: "id"},
    //     {title: "Title", field: "title"}
    //     ]}}
    const valueReModified = companyContextData?.table?.columns?.map((col) => ({
      Header: col?.title,
      accessor: col?.field,
      type: col?.type,
    }));
    // const valueReModified = companyContextData1?.table?.columns?.map((col) => ({
    //     Header: col?.title,
    //     accessor: col?.field,
    //   }));
    setTableMetadata(valueReModified);
  }, [companyContextData]);

  const getCompanyList = async (payloadOverrides = {}) => {
    setLoading(true);
    const payload = {
      ...requestPayload,
      ...payloadOverrides,
    };
    const response = await submitCompanyFilterData(payload);
    const responseData = response?.data || {};
    const listData = responseData?.records || [];
    const valueReModified = listData?.map((item) => ({
      ...item,
      ID: item?.id || item?._id,
      video: "Open",
    }));

    if (response) {
      setCompanyData(valueReModified || []);
      setPaginationMeta({
        pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(responseData?.totalPages) || 0,
        totalRecords: Number(responseData?.totalRecords) || 0,
      });
      setRequestPayload(payload);
    } else {
      setCompanyData([]);
    }
    setLoading(false);
  };

  const handleEdit = (row) => {
    setItemId(row?.id);
    setCompanyAddEditModalOpen(true);
  };

  /** Open company-scoped users list (`user/company/*` APIs). */
  const handleView = (row) => {
    const cid = row?.id || row?._id;
    if (!cid) return;
    navigate(`/${urlEndPoint.companyUser}/${encodeURIComponent(cid)}`);
  };

  const handleFilterSubmit = async (data) => {
    await getCompanyList({
      ...data,
      nameContains: requestPayload?.name || "",
      pageNumber: 1,
    });
    setCommonFilterModalOpen(false);
  };

  const handleSearchSubmit = async (value) => {
    const trimmedValue = value?.trim() || "";
    const nextPayload = {
      ...requestPayload,
      pageNumber: 1,
      nameContains: trimmedValue,
    };
    await getCompanyList(nextPayload);
  };

  const handlePageChange = async (page) => {
    await getCompanyList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getCompanyList({ pageNumber: 1, pageSize: rows });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />

          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
              <h5 className="fw-bold mb-3">Company</h5>              
              {companyActionPermissions.canCreate ? (
                <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                  <Button
                    color="primary"
                    onClick={() => {
                      setItemId(null);
                      setCompanyAddEditModalOpen(true);
                    }}
                  >
                    + Create
                  </Button>
                </Col>
              ) : null}
                            </div>
              <hr />

              <Row className="align-items-center g-2 mb-4">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => {
                        const nextValue = e?.target?.value || "";
                        setSearchQuery(nextValue);
                        if (!nextValue.trim()) {
                          handleSearchSubmit("");
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />

                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>
              </Row>

              {!loading && tableMetaData?.length ? (
                <TableContainer
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyCompanyRowAction}
                  onEdit={
                    companyActionPermissions.canEdit ? handleEdit : undefined
                  }
                  onView={
                    companyActionPermissions.canViewUsers ? handleView : undefined
                  }
                  isCheckboxLeftSide={false}
                  isPagination={true}
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  pageName={"Company"}
                />
              ) : (
                // <Card className="border mb-3">
                //   <CardBody>
                //     <Spinners setLoading={setLoading} />
                //   </CardBody>
                // </Card>
                <Loaders />
              )}
            </CardBody>
          </Card>

          <CompanyAddEditModal
            setCompanyAddEditModalOpen={setCompanyAddEditModalOpen}
            companyAddEditModalOpen={companyAddEditModalOpen}
            getCompanyList={getCompanyList}
            itemId={itemId}
          />
          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="Company"
            handleSubmit={handleFilterSubmit}
            filterData={companyContextData?.filters}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

Company.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(Company);
